<template>
  <div v-show="!isFetchingData">
    <LoadingDialog ref="loading" :loader-msg="$t('book/process:overview.generate-load')"/>

    <v-card class="pa-sm-10 process-handling-bg object-dashboard" elevation="0">
      <v-card-text class="pa-0">
        <v-form ref="form">

          <!-- Overview CardBar -->
          <v-row justify="center" class="mb-3">
            <v-col cols="12" class="pa-0">
              <TaskCard
                  :task-name="process.name"
                  :additionalNames="getAdditionalNames()"
                  :date="computedDate"
                  :task-status="computedTaskStatus"
                  :username="loggedInUser.username"
                  :accepted="computedAccepted"
                  additional-name2-as-btn
                  @clickedAdditional2="showFacility"
              />
            </v-col>
          </v-row>

          <!-- More specific Overview-Card -->
          <v-row justify="space-between" class="overview-card pa-sm-10">
            <!-- Left Side -->
            <v-col cols="12" sm="6" md="5">
              <h4 class="primary--text font-weight-medium">{{ $t('book/process:overview.subtitles.info') }}</h4>
              <LabelField
                  :label="$t('common:name')"
                  :value="process.name"
              />

              <LabelField
                  :label="$t('common:description')"
                  :value="process.description"
              />

              <v-checkbox
                  disabled
                  color="secondary"
                  v-model="processJob.requiresSignature"
                  :label="$t('common:gets-signed')"
              />

              <v-checkbox
                  disabled
                  color="secondary"
                  v-model="processJob.disableDeadline"
                  :label="$t('book/facility:facility.dashboard.information.processes.labels.disable-deadline')"
              />
            </v-col>

            <!-- Right Side -->
            <v-col cols="12" sm="6" md="5">
              <h4 class="primary--text font-weight-medium">{{ $t('book/process:overview.subtitles.required') }}</h4>
              <v-text-field
                  outlined
                  v-model="rideCount"
                  :label="$t('book/process:overview.labels.ride-count')"
                  clearable
                  type="number"
                  :rules="processJob.requiresRideCount ? [ruleRequired($t('utils:rules.required'))] : []"
              />

              <v-text-field
                  outlined
                  v-model="fileNumber"
                  :label="$t('book/process:overview.labels.file-number')"
                  clearable
                  :rules="processJob.requiresFileNumber ? [ruleRequired($t('utils:rules.required'))] : []"
              />

              <v-text-field
                  v-if="quickstart"
                  outlined
                  v-model="loggedInUser.username"
                  :label="$t('book/process:overview.labels.person-in-charge')"
                  :rules="[ruleRequired($t('utils:rules.required'))]"
                  clearable
              />
            </v-col>
          </v-row>
        </v-form>

        <!-- ProcessStepList -->
        <ProcessHandling
            ref="handling"
            @finishedFetchingData="isFetchingData = false"
            @oneStepFailed="setJobState('result-failed')"
            @allSuccessfull="setJobState('result-success')"
            :quickstart="quickstart"
            @finishProcess="finishProcess"
            @cancelProcess="$router.go(-1)"
            @generateReport="generateReport"
        />
      </v-card-text>
    </v-card>

    <!-- Dialog when user tries to exit process handling via route change -->
    <TwoButtonDialog
        ref="cancelBtn"
        :hide-button="true"
        :dialog-title="$t('book/process:overview.dialog.title-cancel2')"
        :dialog-text="$t('book/process:overview.dialog.dialog-cancel2')"
        :route-button-text="$t('book/process:overview.dialog.btn1-text')"
        :button1-text="$t('book/process:overview.dialog.btn1-text')"
        :button2-text="$t('book/process:overview.dialog.btn2-text-no')"
        @clickedButton1="acceptCancel"
        @clickedButton2="declineCancel"
    />

    <!-- Sidebar with general documentations of processJob -->
    <ProcessDocuDrawer
        v-if="!quickstart"
        ref="docuDrawer"
        @viewDocument="viewDocumentation"
        @downloadDocument="downloadDocumentation"
    />

    <DocumentViewer
        documentType="STEP"
        v-if="!quickstart"
        ref="documentViewer"
    />

    <ExternalFacilityDialog ref="facilityDialog"/>
  </div>
</template>
<script>
import TaskCard from "@/modules/book/components/TaskCard/TaskCard";
import ProcessDocuDrawer from "@/modules/book/views/process/ProcessDocuDrawer";
import DocumentViewer from "@/modules/core/components/DocumentViewer/DocumentViewer";
import TwoButtonDialog from "@/modules/core/components/TwoButtonDialog/TwoButtonDialog";
import Process from "@/modules/book/data/process";
import ProcessService from "@/modules/book/services/process/impl/process-service";
import DataUtils from "@/modules/core/services/data-utils";
import ProcessJobService from "@/modules/book/services/process/impl/process-job-service";
import ProcessJob from "@/modules/book/data/processJob";
import UsersService from "@/modules/core/services/users-service";
import Users from "@/modules/core/data/users";
import Utils from "@/modules/core/services/utils";
import FacilityService from "@/modules/book/services/facility-service";
import Vue from "vue";
import Facility from "@/modules/book/data/facility";
import DocumentationService from "@/modules/core/services/documentation-service";
import Documentation from "@/modules/book/data/documentation";
import LabelField from "@/modules/core/components/LabelField";
import {ruleRequired} from "@/modules/core/services/rules";
import indexedDBService from "@/modules/core/services/indexedDB-service";
import DateUtils from "@/modules/core/services/date-utils";
import ProcessHandling from "@/modules/book/views/process/ProcessHandling";
import ProcessResultService from "@/modules/book/services/process/impl/process-result-service";
import PdfUtils from "@/modules/core/services/pdf-utils";
import LoadingDialog from "@/modules/core/components/Loader/LoadingDialog";
import {DocumentType} from '@/modules/book/data/document-type.js';
import {mapGetters} from "vuex";
import ExternalFacilityDialog from "@/modules/book/components/Facility/ExternalFacilityDialog";

export default {
  components: {
    ExternalFacilityDialog,
    LoadingDialog, ProcessHandling, LabelField, TwoButtonDialog, DocumentViewer, ProcessDocuDrawer, TaskCard},
  data () {
    return {
      process: new Process(),
      processJob: new ProcessJob(),
      loggedInUser: new Users(),
      facility: new Facility(),
      rideCount: null,
      fileNumber: null,
      restDocu: [],

      // data for navigation guard
      next: null,
      allowBack: false,

      isFetchingData: true,
      quickstart: false// if it is a quickstarted processJob
    }
  },
  computed: {
    computedBuildingName() {
      if (this.quickstart) {
        return this.facility.buildingName;
      } else {
        return this.processJob.buildingName;
      }
    },
    computedFacilityName() {
      if (this.quickstart) {
        return this.facility.name;
      } else {
        return this.processJob.facilityName;
      }
    },
    computedDate() {
      if (this.quickstart) {
        return DateUtils.getCurrentDateAsDEString();
      } else {
        return this.processJob.date;
      }
    },
    computedTaskStatus() {
      if (this.quickstart) {
        return this.process.taskStatus;
      } else {
        return this.processJob.taskStatus;
      }
    },
    computedAccepted() {
      if (this.quickstart) {
        return true
      } else {
        return this.processJob.accepted;
      }
    },
    currentLang() {
      return this.$store.state.translation.language;
    },
    ...mapGetters('auth', ['isWorker']),
  },
  methods: {
    showFacility() {
      let facilityId;
      if (this.quickstart) {
        facilityId = this.facility.id;
      } else {
        facilityId = this.processJob.facility
      }

      if (this.isWorker) {
        // show basic facility dialog
        this.$refs.facilityDialog.initDialog(facilityId);
      } else {
        // route to facility
        this.$router.push(`/${this.$store.state.manager.bookModuleUrl}/facilities/edit/${facilityId}`)
      }
    },

    ruleRequired,
    async readProcess(processID) {
      if (processID) {
        await ProcessService.readFromAPI(processID,
            (res) => {
              this.process = DataUtils.parseDataToSingleObject(res.data, Process);
            },
            (error) => {
              this.$logger.error(error);
              this.$router.push('/404')
            }
        )
      }
    },

    downloadDocumentation(data) {
      DocumentationService.downloadFile(data.id, data.fileName, data.fileType, this.facility.id, data.documentType);
    },

    viewDocumentation(data) {
      this.$refs.documentViewer.initViewerById(this.restDocu, data.id, this.facility.id, data.documentType);
    },

    // methods for canceling process
    cancelProcess(path) {
      this.$refs.cancelBtn.showDialog = true;
      this.$refs.cancelBtn.toRoute = path;
    },
    acceptCancel() {
      this.$refs.cancelBtn.showDialog = false;
      // remove all processResults from the indexedDB
      indexedDBService.clearObjectStore('processResult')
      // remove all processStepResults from the indexedDB
      indexedDBService.clearObjectStore('processStepsAndResults')

      this.$store.commit('book/process/storeUsername', null)
      this.$store.commit('book/process/storeRideCount', null)
      this.$store.commit('book/process/storeFileNumber', null)
      this.$store.commit('book/process/storeFacility', null)

      this.next();
    },
    declineCancel() {
      this.$refs.cancelBtn.showDialog = false;
    },

    async postDocusFromSteps(processStepsAndResults) {
      let promises = [];

      for (let i = 0; i < processStepsAndResults.length; i++) {
        if (!processStepsAndResults.success) {
          for (let j = 0; j < processStepsAndResults[i].documentation.length; j++) {
            let docu = new Documentation();
            docu.name = processStepsAndResults[i].documentation[j].name;

            if (processStepsAndResults[i].documentation[j].startDate) {
              docu.startDate = processStepsAndResults[i].documentation[j].startDate
            } else {
              docu.startDate = "";
            }

            if (processStepsAndResults[i].documentation[j].endDate) {
              docu.endDate = processStepsAndResults[i].documentation[j].endDate
            } else {
              docu.endDate = "";
            }

            promises.push(
                new Promise(resolve => {
                  DocumentationService.postFileToAPI(
                      processStepsAndResults[i].documentation[j].file,
                      docu,
                      DocumentType.STEP,
                      this.facility && this.facility.id ? this.facility.id : this.processJob.facility,
                      (res) => {
                        processStepsAndResults[i].documentation[j].id = res.data.id;
                        resolve();
                      },
                      (error) => {
                        resolve();
                        Utils.showFailureSnackbar(this.$t('snackbar:upload-failure', {name: this.$t('common:documentation')}))
                        this.$logger.error(error);
                      }
                  )
                })
            )
          }
        }
      }

      await Promise.all(promises);
    },
    generateReport() {
      if (window.navigator.onLine) {

        if (!this.$refs.form.validate()) {
          return;
        }
        this.$refs.loading.openDialog();

        // read the processResult from the indexedDB
        indexedDBService.read(this.quickstart ? this.process.id : this.processJob.result, 'processResult').then((processResult) => {
          // read all processStepResults from the indexedDB
          indexedDBService.readAll('processStepsAndResults').then((processStepsAndResults) => {
            // post all added documentations
            this.postDocusFromSteps(processStepsAndResults).then(() => {
              // set the id of the posted documentations to the processResult stepResults documentations
              for (let i = 0; i < processResult.stepResults.length; i++) {
                for (let j = processStepsAndResults.length - 1; j >= 0; j--) {
                  if (processResult.stepResults[i].step === processStepsAndResults[j].id) {
                    for (let k = 0; k < processStepsAndResults[j].documentation.length; k++) {
                      if (processResult.stepResults[i].documentation[k]) {
                        processResult.stepResults[i].documentation[k] = processStepsAndResults[j].documentation[k].id
                      }
                    }
                  }
                }
              }
              processResult.fileNumber = this.fileNumber;
              processResult.rideCount = this.rideCount;

              // update the processResult and generate a report for it
              ProcessJobService.updateAndGenerateReport(this.$route.params.processID, processResult,
                  (result) => {
                    this.$refs.loading.closeDialog();
                    PdfUtils.openPDFInNewTabSetFileType(result.data);
                  },
                  (error) => {
                    this.$refs.loading.closeDialog();
                    this.$logger.error("ProcessResult could not be updated and report not being generated, because: " + error);
                    Utils.showFailureSnackbar(this.$t("book/process:overview.report-gen-failed"))
                  }
              )
            })
          })
        })
      } else {
        Utils.showWarningSnackbar(this.$t('book/process:overview.no-internet'))
      }
    },

    // finishes whole Process
    finishProcess() {
      // if the user has a internet connection
      // he can post everything
      if (window.navigator.onLine) {
        if (!this.$refs.form.validate()) {
          this.$refs.handling.$refs.finishBtn.setButtonSettings(false, false, false, false);
          this.$refs.handling.$refs.finishBtn.showDialog = false;

          window.scrollTo(0, 0);

          return;
        }

        this.allowBack = true;
        this.$refs.handling.$refs.finishBtn.setButtonSettings(true, true, false, true);

        // read the processResult from the indexedDB
        indexedDBService.read(this.quickstart ? this.process.id : this.processJob.result, 'processResult').then((processResult) => {
          // read all processStepResults from the indexedDB
          indexedDBService.readAll('processStepsAndResults').then((processStepsAndResults) => {
            // post all added documentations
            this.postDocusFromSteps(processStepsAndResults).then(() => {
              // set the id of the posted documentations to the processResult stepResults documentations
              for (let i = 0; i < processResult.stepResults.length; i++) {
                for (let j = processStepsAndResults.length - 1; j >= 0; j--) {
                  if (processResult.stepResults[i].step === processStepsAndResults[j].id) {
                    for (let k = 0; k < processStepsAndResults[j].documentation.length; k++) {
                      if (processResult.stepResults[i].documentation[k]) {
                        processResult.stepResults[i].documentation[k] = processStepsAndResults[j].documentation[k].id
                      }
                    }
                  }
                }
              }

              if (this.quickstart) {
                // empty the id so a new processResult will be created in the backend for the process
                processResult.id = null;
                processResult.rideCount = this.rideCount;
                processResult.fileNumber = this.fileNumber;
                processResult.username = this.loggedInUser.username;

                // check if stepResults are all positive or one step failed
                let successCounter = 0;
                for(let i = 0; i < processResult.stepResults.length; i++) {
                  if(processResult.stepResults[i].success) {
                    successCounter++
                  }
                }
                if(successCounter === processResult.stepResults.length) {
                  processResult.success = true;
                }

                ProcessResultService.postQuickstartToAPI(processResult,
                    () => {
                      this.allowBack = true;
                      Utils.showSuccessSnackbar(this.$t('snackbar:finish-success', {name: this.$t('common:process')}));
                      this.$router.go(-1);
                    },
                    () => {
                      this.$refs.handling.$refs.finishBtn.setButtonSettings(false, false, false, false);
                      this.$refs.handling.$refs.finishBtn.showDialog = false;
                      Utils.showFailureSnackbar(this.$t('snackbar:finish-failure', {name: this.$t('common:process')}))
                    }
                )
              } else {
                // update the processResult so it is up to date
                ProcessJobService.updateProcessJob(this.$route.params.processID, processResult,
                    () => {
                      // finally close the processJob
                      ProcessJobService.closeProcessJob(this.$route.params.processID, this.rideCount, this.fileNumber,
                          () => {
                            Utils.showSuccessSnackbar(this.$t('snackbar:finish-success', {name: this.$t('common:process')}));
                            this.$router.go(-1);
                          },
                          (serviceName, error) => {

                            if (error.response.headers != null &&
                                error.response.headers.error_cause && this.$t('error-causes:' + error.response.headers.error_cause)) {

                              this.$refs.handling.$refs.finishBtn.setButtonSettings(false, false, false, false);
                              this.$refs.handling.$refs.finishBtn.showDialog = false;
                              Utils.showFailureSnackbar(this.$t('error-causes:' + error.response.headers.error_cause));
                            }
                            else {
                              this.$refs.handling.$refs.finishBtn.setButtonSettings(false, false, false, false);
                              this.$refs.handling.$refs.finishBtn.showDialog = false;
                              Utils.showFailureSnackbar(this.$t('snackbar:finish-failure', {name: this.$t('common:process')}) + ' (' + error.message + ')');
                            }
                          }
                      )
                    },
                    (error) => {
                      this.$logger.error("ProcessResult could not be updated because: " + error);
                    }
                )
              }
            })
          })
        })
      } else {
        Utils.showWarningSnackbar(this.$t('book/process:overview.no-internet'))
      }
    },

    setJobState(value) {
      Vue.set(this.quickstart ? this.process : this.processJob, 'taskStatus', value)
    },

    getAdditionalNames() {
      return [
        {name: this.computedBuildingName, asButton: false},
        {name: this.computedFacilityName, asButton: false}
      ]
    }
  },
  watch: {
    'loggedInUser.username' () {
      if (this.loggedInUser.username !== '') {
        this.$store.commit('book/process/storeUsername', this.loggedInUser.username)
      }
    },

    rideCount() {
      if (this.rideCount !== null) {
        this.$store.commit('book/process/storeRideCount', this.rideCount);
      }
    },

    fileNumber() {
      if (this.fileNumber !== null) {
        this.$store.commit('book/process/storeFileNumber', this.fileNumber);
      }
    },

    currentLang() {
      Utils.sleep(500).then(() => {
        this.$refs.form.resetValidation()
      });
    }
  },
  beforeRouteLeave(to, from, next) {
    this.next = next;

    if (this.allowBack === true || to.name === '404') {
      // remove all processResults from the indexedDB
      indexedDBService.clearObjectStore('processResult')
      // remove all processStepResults from the indexedDB
      indexedDBService.clearObjectStore('processStepsAndResults')
      next();
    } else {
      this.cancelProcess(to.path);
      next(false);
    }
  },
  created() {
    this.rideCount = this.$store.state.book.process.rideCount;
    this.fileNumber = this.$store.state.book.process.fileNumber;

    if (this.$route.name === 'ProcessQuickstartOverview') {
      this.quickstart = true;
    } else {
      this.quickstart = false;
    }

    if (this.quickstart) {
      this.readProcess(this.$route.params.processID).then(() => {
        if (this.$route.params.facilityID) {
          FacilityService.readFromAPI(this.$route.params.facilityID,
              (res) => {
                this.facility = DataUtils.parseDataToSingleObject(res.data, Facility);
                this.setJobState('job-accepted-valid')

                this.$refs.handling.init(this.process, null, this.facility)
              }
          )
        }
      })
    } else {
      ProcessJobService.readAccessibleForHandlingJob(this.$route.params.processID,
          (res) => {
            this.processJob = DataUtils.parseDataToSingleObject(res.data, ProcessJob);
            this.setJobState('job-accepted-valid')

            // if processJob has documentations, load them and insert them into docuSidebar
            if (this.processJob.documentations && this.processJob.documentations.length > 0) {
              DocumentationService.readInfoListFromApi(this.processJob.documentations,
                  (resp) => {
                    this.restDocu = DataUtils.parseDataToObjectArray(resp.data, Documentation);
                    this.$refs.docuDrawer.documentations = DataUtils.parseDataToObjectArray(resp.data, Documentation);
                  },
                  (error) => {
                    this.$logger.error(error);
                  Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:documentation_plural')}))
                  }
              )
            }

            this.readProcess(this.processJob.process).then(() => {
              this.$refs.handling.init(this.process, this.processJob, null)
            })
          }
      )
    }

    // read profile of current logged in user
    UsersService.readProfileFromAPI(
        (res) => {
          this.loggedInUser = DataUtils.parseDataToSingleObject(res.data, Users);
        },
        (error) => {
          Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('common:user')}))
          this.$logger.error(error);
        }
    )
  }
}
</script>
<style src="@/modules/book/views/process/Process.scss" lang="scss"/>